import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

const fmtDate = (s) => { if (!s) return null; try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; } };

// Carte equipe : attribution du maitre d'oeuvre + liste des comptes rendus de reunion
const MoeSection = ({ projectId, project, canEdit, onChanged }) => {
  const [moeUsers, setMoeUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [form, setForm] = useState({ name: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [openCr, setOpenCr] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/moe/users`).then(r => setMoeUsers(r.data.items || [])).catch(() => {});
    axios.get(`${API}/moe/reports/${projectId}`).then(r => setReports(r.data.items || [])).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const assign = async (name, email) => {
    if (!email.trim()) { alert("Renseignez l'email du maître d'œuvre."); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/moe/assign/${projectId}`, { email: email.trim(), name: name.trim() || null });
      if (r.data.created_new) alert(`Compte créé : un email d'invitation avec mot de passe temporaire a été envoyé à ${r.data.email}.`);
      setForm({ name: '', email: '' });
      onChanged && onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur lors de l'attribution");
    } finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!window.confirm(`Retirer l'accès de ${project.moe_name || project.moe_email} à ce projet ?`)) return;
    try { await axios.delete(`${API}/moe/assign/${projectId}`); onChanged && onChanged(); }
    catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-4" data-testid="moe-section">
      {project.moe_user_id ? (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid="moe-assigned">
          <div>
            <p className="text-sm font-semibold text-gray-900">{project.moe_name}</p>
            <p className="text-xs text-gray-500">{project.moe_email} — accès portail MOE (projet, travaux, documents, CR)</p>
          </div>
          {canEdit && (
            <button onClick={revoke} className="text-xs px-2.5 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-semibold" data-testid="moe-revoke-btn">Retirer</button>
          )}
        </div>
      ) : canEdit ? (
        <div className="space-y-2" data-testid="moe-assign-form">
          {moeUsers.length > 0 && (
            <div>
              <p className="text-[10px] uppercase text-gray-400 font-semibold mb-1">Maîtres d'œuvre existants — 1 clic pour attribuer</p>
              <div className="flex flex-wrap gap-1.5">
                {moeUsers.map(m => (
                  <button key={m.user_id} onClick={() => assign(m.name || '', m.email)} disabled={saving}
                          className="text-xs px-2 py-1 rounded-full border bg-white border-gray-300 text-gray-700 hover:border-[#13736D] hover:text-[#13736D]"
                          data-testid={`moe-chip-${m.user_id}`}>{m.name} ({m.email})</button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="text" placeholder="Nom du maître d'œuvre" value={form.name}
                   onChange={e => setForm({ ...form, name: e.target.value })}
                   className="realease-input text-sm" data-testid="moe-name-input" />
            <input type="email" placeholder="Email (invitation envoyée)" value={form.email}
                   onChange={e => setForm({ ...form, email: e.target.value })}
                   className="realease-input text-sm" data-testid="moe-email-input" />
          </div>
          <button onClick={() => assign(form.name, form.email)} disabled={saving}
                  className="text-sm px-4 py-1.5 rounded-lg bg-[#13736D] hover:bg-[#0f5f5a] text-white font-semibold disabled:opacity-50"
                  data-testid="moe-assign-btn">{saving ? 'Attribution…' : "Attribuer le maître d'œuvre"}</button>
          <p className="text-[11px] text-gray-400">Si le compte n'existe pas, il est créé et un email d'invitation avec mot de passe temporaire est envoyé. Le MOE voit le projet (vue client, sans analyses ni messagerie) et rédige des comptes rendus de réunion.</p>
        </div>
      ) : <p className="text-xs text-gray-400 italic">Aucun maître d'œuvre attribué.</p>}

      <div data-testid="moe-reports-internal">
        <p className="text-sm font-semibold text-gray-800 mb-2">Comptes rendus de réunion du MOE ({reports.length})</p>
        {reports.length === 0 ? (
          <p className="text-xs text-gray-400 italic">Aucun compte rendu pour le moment.</p>
        ) : (
          <div className="space-y-1.5">
            {reports.map(cr => (
              <div key={cr.cr_id} className="border border-gray-100 rounded-lg" data-testid={`moe-cr-${cr.cr_id}`}>
                <button onClick={() => setOpenCr(openCr === cr.cr_id ? null : cr.cr_id)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-left">
                  <span className="text-sm font-medium text-gray-800 truncate">{cr.title}</span>
                  <span className="flex items-center gap-2 shrink-0 ml-2">
                    {cr.shared_with_client && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Partagé client</span>}
                    <span className="text-xs text-gray-400">{fmtDate(cr.meeting_date) || fmtDate(cr.created_at)}</span>
                  </span>
                </button>
                {openCr === cr.cr_id && (
                  <div className="px-3 pb-3 text-sm text-gray-700">
                    {cr.participants && <p className="text-xs text-gray-500 mb-1">Participants : {cr.participants}</p>}
                    <p className="whitespace-pre-wrap bg-gray-50 rounded p-2.5 text-[13px]">{cr.content}</p>
                    {(cr.photos || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {cr.photos.map(ph => (
                          <a key={ph.photo_id} href={`${API}/moe/photos/${ph.photo_id}`} target="_blank" rel="noreferrer">
                            <img src={`${API}/moe/photos/${ph.photo_id}`} alt={ph.filename}
                                 className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                                 data-testid={`moe-internal-photo-${ph.photo_id}`} />
                          </a>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-gray-400 mt-1">Rédigé par {cr.author_name} le {fmtDate(cr.created_at)}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MoeSection;
